import React, { useState, useEffect } from 'react';
import { Theme, makeStyles } from '@material-ui/core/styles';
import { Box, Button, Grid, List, ListItem, Paper, Typography, useTheme } from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useHistory, useParams } from 'react-router-dom';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import { member as v_member, basic, Values, SNACKBAR_TIME, FINISH_TIME } from '../../../request/values';
import { COLORS } from '../../../shared/Theme';
import DialogConfirm, { DialogSaleApprovalPrice } from '../../../components/Dialog';
import ToastStr from '../../../request/toastStr';
import { TitleBackBar } from '../../../shared/header/TitleBar';
import { formatMoneyAmount, getTranslatedMaterialName } from '../../../locales/formatModule';

const SERVER = basic.server;

const useStyles = makeStyles((theme: Theme) => ({
	wrapper: {
		paddingTop: '10px',
		paddingLeft: '0',
		paddingRight: '0',
		[theme.breakpoints.up('md')]: {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			paddingTop: '40px',
			paddingLeft: '20px',
			paddingRight: '20px',
			paddingBottom: theme.spacing(4),
		},
	},
	materialItem: {
		borderRadius: 12,
		margin: 'auto',
		marginBottom: '1.5vh',

		boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
		width: '82vw',
		paddingTop: '2.2vh',
		paddingBottom: '2.2vh',
		[theme.breakpoints.up('md')]: {
			width: '98%',
			paddingTop: '10px',
			paddingBottom: '10px',
		},
	},
	list: {
		width: '95vw',
		height: '68vh',
		overflowY: 'auto',
		...theme.typography.subtitle1,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		color: `${COLORS.BROWNISH_GREY}`,
		[theme.breakpoints.up('md')]: {
			width: '100%',
			height: '100%',
			marginTop: theme.spacing(1),
			marginBottom: theme.spacing(2),
		},
	},
	materialImage: {
		textAlign: 'center',
		margin: 'auto',
		'& img': {
			width: '11vw',
			maxWidth: '60px',
		},
	},

	materialDesc: {
		paddingLeft: '1vw',
		'& #materialName': {
			...theme.typography.subtitle2,
			fontFamily: 'NanumB',
			fontWeight: 'bold',
			color: `${COLORS.BROWNISH_GREY}`,
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
		},
		'& #materialSubName': {
			...theme.typography.body2,
			fontFamily: 'NanumB',
			fontWeight: 'bold',
			color: `${COLORS.BROWNISH_GREY}`,
		},
	},
	actionBar: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 12,
		width: '82vw',
		margin: '8px auto 0',
		padding: '8px 0 12px',
		[theme.breakpoints.up('md')]: {
			width: '98%',
			gap: 16,
			padding: '16px 0 8px',
		},
	},
	actionButton: {
		flex: 1,
		minHeight: 48,
		padding: '10px 12px',
		borderRadius: 10,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		fontSize: '1rem',
		lineHeight: 1.2,
		boxShadow: 'none',
		[theme.breakpoints.up('md')]: {
			minHeight: 52,
			fontSize: '1.1rem',
		},
	},
	declineButton: {
		backgroundColor: COLORS.WHITE,
		color: COLORS.RED,
		border: `1.5px solid ${COLORS.RED}`,
		'&:hover': {
			backgroundColor: COLORS.VERY_LIGHT_RED,
		},
		'@media (hover: none)': {
			'&:hover': {
				backgroundColor: COLORS.WHITE,
			},
		},
	},
	approveButton: {
		backgroundColor: COLORS.WATER_BLUE,
		color: COLORS.WHITE,
		boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
		'&:hover': {
			backgroundColor: '#0f7bbd',
		},
		'@media (hover: none)': {
			'&:hover': {
				backgroundColor: COLORS.WATER_BLUE,
			},
		},
	},
	numberDesc: {
		paddingLeft: '1vw',
		alignItems: 'end',
		'& #materialName': {
			...theme.typography.subtitle2,
			fontFamily: 'NanumB',
			fontWeight: 'bold',
			color: `${COLORS.BROWNISH_GREY}`,
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
		},
		'& #materialSubName': {
			...theme.typography.body2,
			fontFamily: 'NanumB',
			fontWeight: 'bold',
			color: `${COLORS.BROWNISH_GREY}`,
		},
	},
	paper: {
		boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.16)',
		padding: theme.spacing(1),
		color: `${COLORS.BROWNISH_GREY}`,
	},
	grid: {
		width: '95%',
		marginTop: theme.spacing(1),
	},
	numberText: {
		...theme.typography.h2,
		fontFamily: 'NanumB',
		fontWeight: 'bold',
		[theme.breakpoints.up('lg')]: {
			...theme.typography.body2,
			fontFamily: 'NanumB',
			fontWeight: 'bold',
		},
	},
}));

const MaterialSaleSheet: React.FC<any> = (props) => {
	const classes = useStyles();
	const history = useHistory();
	const theme = useTheme();
	const isLargeUp = useMediaQuery(theme.breakpoints.up('lg'));
	const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { t } = useTranslation('student');

	const [materialSale, setMaterialSale] = useState([]);
	const { member } = props;

	const [approvalDialog, setApprovalDialog] = useState<boolean | number>(false);
	const [declineDialog, setDeclineDialog] = useState<boolean | number>(false);

	const state: any = history.location.state;
	const sale = state.sale.materialOrderNum;
	const orderCompanyNum = state.sale.companyNum;
	const orderCompanyName = state.sale.companyName;

	const handleClose = (key: string) => {
		if (key === 'approvalDialog') setApprovalDialog((prevState) => false);
		else if (key === 'declineDialog') {
			setDeclineDialog((prevState) => false);
		}
	};

	const handleApprovalOpen = () => {
		console.log(state.sale.companyName);
		if (approvalDialog === false) {
			setApprovalDialog((prevState) => true);
		}
	};

	const handleDeclineOpen = () => {
		if (declineDialog === false) {
			setDeclineDialog((prevState) => true);
		}
	};

	const postMemberExit = () => {
		axios({
			method: 'POST',
			data: {
				materialSaleNum: sale,
				email: member.id,
			},
			withCredentials: true,
			url: SERVER + v_member.member + v_member.material_sale_sheet_exit,
		});
	};

	const getMaterialSaleSheetList = () => {
		console.log(sale);
		let url = new URL(basic.server + v_member.member + v_member.material_sale_sheet_list);
		let param = {
			materialSaleNum: sale,
			email: member.id,
			classVersion: member.classVersion,
		};
		url.search = new URLSearchParams(param).toString();

		axios({
			method: 'GET',
			withCredentials: true,
			url: url.toString(),
		})
			.then((res) => res.data)
			.then((data) => {
				console.log(data);

				if (data.code === Values.SUCCESS_CODE) {
					if (data.materials === null) {
						enqueueSnackbar(ToastStr.MATERIAL_SHEET_LIST_EMPTY_STR, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
					} else if (data.materials.length === 0) {
						enqueueSnackbar(ToastStr.MATERIAL_SHEET_LIST_EMPTY_STR, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
					} else {
						setMaterialSale((prevState) => data.materials);
					}
				} else if (data.code === Values.FAIL_CODE) {
					enqueueSnackbar(ToastStr.MATERIAL_SHEET_DUPLICATE_STR, {
						variant: 'warning',
						autoHideDuration: SNACKBAR_TIME,
					});
				}
			});
	};

	const postUserSaleUpdate = () => {
		const totalPrice = materialSale.map((item: any) => item.materialPrice * item.materialCount).reduce((prev, curr) => prev + curr, 0);

		const finalPrice = Math.floor(totalPrice * 1.05);
		let url;
		url = SERVER + v_member.member + v_member.material_sale_approval;

		if (approvalDialog !== false) {
			axios({
				method: 'POST',
				data: {
					companyNum: member.companyNum,
					orderCompanyNum: orderCompanyNum,
					materialSaleNum: sale,
					totalPrice: finalPrice,
					memberId: member.id,
				},
				withCredentials: true,
				url: url,
			})
				.then((res) => {
					return res.data;
				})
				.then((data: any) => {
					console.log(data);
					if (data.code === Values.SUCCESS_CODE) {
						enqueueSnackbar(ToastStr.MATERIAL_APPROVAL_SUCCESS_STR, {
							variant: 'success',
							autoHideDuration: SNACKBAR_TIME,
						});
						history.push('/MaterialSale');
					} else if (data.code === Values.SALE_FINISH_CODE) {
						enqueueSnackbar(ToastStr.MATERIAL_SALE_FINISH, {
							variant: 'warning',
							autoHideDuration: FINISH_TIME,
						});
						history.push('/MaterialSale');
					} else if (data.code === Values.FAIL_LOCK_CODE) {
						enqueueSnackbar(ToastStr.MATERIAL_SALE_LOCK, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
						history.push('/MaterialSale');
					} else if (data.code === Values.FAIL_CODE) {
						enqueueSnackbar(ToastStr.MATERIAL_APPROVAL_FAIL_STR, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
					} else {
						enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
							variant: 'error',
							autoHideDuration: SNACKBAR_TIME,
						});
					}
				});
		}
		handleClose('approvalDialog');
	};

	const postDeclineSale = () => {
		let url = SERVER + v_member.member + v_member.material_sale_refuse;

		if (declineDialog !== false) {
			axios({
				method: 'POST',
				data: {
					materialOrderNum: sale,
					classVersion: member.classVersion,
					memberId: member.id,
				},
				withCredentials: true,
				url: url,
			})
				.then((res) => {
					return res.data;
				})
				.then((data: any) => {
					console.log(data);
					if (data.code === Values.SUCCESS_CODE) {
						enqueueSnackbar(ToastStr.MATERIAL_DECLINE_SUCCESS_STR, {
							variant: 'success',
							autoHideDuration: SNACKBAR_TIME,
						});
						history.push('/MaterialSale');
					} else if (data.code === Values.FAIL_LOCK_CODE) {
						enqueueSnackbar(ToastStr.MATERIAL_SALE_LOCK, {
							variant: 'warning',
							autoHideDuration: SNACKBAR_TIME,
						});
						history.push('/MaterialSale');
					} else if (data.code === Values.SALE_FINISH_CODE) {
						enqueueSnackbar(ToastStr.MATERIAL_SALE_FINISH, {
							variant: 'warning',
							autoHideDuration: FINISH_TIME,
						});
						history.push('/MaterialSale');
					} else {
						enqueueSnackbar(ToastStr.REQUEST_GET_POST_FAIL_STR, {
							variant: 'error',
							autoHideDuration: SNACKBAR_TIME,
						});
					}
				});
		}
		handleClose('declineDialog');
	};

	useEffect(() => {
		getMaterialSaleSheetList();
		return () => {
			postMemberExit();
		};
	}, []);

	const actionButtons = (
		<div className={classes.actionBar}>
			<Button className={`${classes.actionButton} ${classes.declineButton}`} onClick={handleDeclineOpen} data-testid="dist-material-sale-decline-btn">
				{t('MaterialSaleSheet.decline')}
			</Button>
			<Button className={`${classes.actionButton} ${classes.approveButton}`} onClick={handleApprovalOpen} data-testid="dist-material-sale-approve-btn">
				{t('MaterialSaleSheet.approve')}
			</Button>
		</div>
	);

	return (
		<div className="contentList">
			<div className={classes.wrapper}>
				<TitleBackBar history={history} title={orderCompanyName} titleIcon="order" />
				{isLargeUp ? (
					<Grid container className={classes.grid} spacing={3}>
						{materialSale != null &&
							materialSale.map((item: any, index: number) => {
								return (
									<Grid item xs={6} key={index}>
										<Paper className={classes.paper}>
											<MaterialSaleItem {...item} />
										</Paper>
									</Grid>
								);
							})}
					</Grid>
				) : (
					<Box display="flex" justifyContent="center" alignItems="center">
						<List className={classes.list}>
							{materialSale != null &&
								materialSale.map((item: any, index: number) => {
									return (
										<ListItem className={classes.materialItem} alignItems="center">
											<MaterialSaleItem {...item} />
										</ListItem>
									);
								})}
						</List>
					</Box>
				)}
				{actionButtons}
				<DialogSaleApprovalPrice
					open={approvalDialog === false ? false : true}
					handleClose={() => handleClose('approvalDialog')}
					companyName={orderCompanyName}
					totalPrice={formatMoneyAmount(
						Math.floor(materialSale.map((item: any) => item.materialPrice * item.materialCount).reduce((prev, curr) => prev + curr, 0) * 1.05),
					)}
					noConfirm={() => handleClose('approvalDialog')}
					yesConfirm={postUserSaleUpdate}
				/>
				<DialogConfirm
					open={declineDialog === false ? false : true}
					handleClose={() => handleClose('declineDialog')}
					noConfirm={() => handleClose('declineDialog')}
					yesConfirm={postDeclineSale}
					title={t('MaterialSale.pageTitle')}
					context={t('MaterialSaleSheet.declineConfirmTitle')}
					noText={t('MaterialSaleSheet.declineConfirmNo')}
					yesText={t('MaterialSaleSheet.declineConfirmYes')}
				/>
			</div>
		</div>
	);
};

const MaterialSaleItem: React.FC<any> = (props) => {
	const classes = useStyles();
	const { t } = useTranslation('student');
	const { t: tObject } = useTranslation('object');
	const materialName = getTranslatedMaterialName(tObject, props.materialCode.toString(), props.materialName);

	return (
		<Grid container alignContent="center">
			<Grid xs={2} className={classes.materialImage}>
				<img src={basic.server + '/displayImage?fileName=' + props.materialImage} />
			</Grid>
			<Grid xs={7} style={{ margin: 'auto' }}>
				<Grid container className={classes.materialDesc} direction="column">
					<Grid id="materialName">{materialName}</Grid>
					<Grid id="materialSubName">
						{props.materialSubName} {props.materialCode}
					</Grid>
				</Grid>
			</Grid>
			<Grid xs={2} style={{ margin: 'auto' }}>
				<Grid container className={classes.numberDesc} direction="column">
					<Grid id="materialName">
						<Typography
							display="inline"
							className={classes.numberText}
							style={{
								color: `${COLORS.WATER_BLUE}`,
							}}
						>
							{formatMoneyAmount(props.materialPrice)}
						</Typography>
					</Grid>
					<Grid id="materialSubName">
						<Typography
							display="inline"
							className={classes.numberText}
							style={{
								color: `${COLORS.WATER_BLUE}`,
							}}
						>
							{`${props.materialCount} `}
						</Typography>
						<Typography
							display="inline"
							className={classes.numberText}
							style={{
								color: `${COLORS.BROWNISH_GREY}`,
							}}
						>
							{t('MaterialSaleSheet.countSuffix')}
						</Typography>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default MaterialSaleSheet;
